import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class ApiInterceptor implements HttpInterceptor {
  private isRefreshingToken = false;
  private cachedRequests: Array<{ req: HttpRequest<any>, next: HttpHandler }> = [];

  constructor(private api: ApiService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Always get the latest token from localStorage (kept fresh by onIdTokenChanged)
    const token = this.api.getToken();

    if (req.headers.get('skip')) {
      return next.handle(req.clone({ headers: req.headers.delete('skip') }));
    }

    req = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });

    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && !this.isRefreshingToken) {
          // Cache the failed request
          this.cachedRequests.push({ req, next });

          return from(this.getNewToken()).pipe(
            switchMap(newToken => {
              localStorage.setItem('GProToken', JSON.stringify(newToken));
              this.processCachedRequests(newToken);
              return next.handle(req.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` }
              }));
            }),
            catchError(refreshError => {
              return throwError(() => refreshError);
            })
          );
        }

        // If we're already refreshing, store the requests for later processing
        if (err.status === 401 && this.isRefreshingToken) {
          this.cachedRequests.push({ req, next });
          return throwError(() => err);
        }

        return throwError(() => err);
      })
    );
  }

  // Only used for 401 retry, not for periodic refresh
  private async getNewToken(): Promise<string> {
    const { getAuth, onAuthStateChanged } = await import('firebase/auth');
    const auth = getAuth();
    return new Promise<string>((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe();
        if (!user) {
          return reject(new Error('No user is currently authenticated'));
        }
        try {
          this.isRefreshingToken = true;
          const newToken = await user.getIdToken(true); // Force refresh token
          this.isRefreshingToken = false;
          resolve(newToken);
        } catch (error) {
          this.isRefreshingToken = false;
          reject(error);
        }
      });
    });
  }

  // Process cached requests with the refreshed token
  private processCachedRequests(newToken: string) {
    this.cachedRequests.forEach(({ req, next }) => {
      const clonedReq = req.clone({
        setHeaders: { Authorization: `Bearer ${newToken}` }
      });
      next.handle(clonedReq).subscribe();
    });
    this.cachedRequests = [];
  }
}
